import { Skeleton } from './Skeleton';

/* ─── Column definition ────────────────────────────────────────────────────── */

export type Column<T> = {
  key: string;
  header: string;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  render: (row: T, index: number) => React.ReactNode;
};

type Props<T> = {
  columns: Column<T>[];
  rows: T[];
  getKey: (row: T, index: number) => string | number;
  onRowClick?: (row: T, index: number) => void;
  loading?: boolean;
  emptyMessage?: string;
  emptyDescription?: string;
};

/* ─── Table ─────────────────────────────────────────────────────────────────── */

export function Table<T>({
  columns,
  rows,
  getKey,
  onRowClick,
  loading,
  emptyMessage = 'Sin resultados',
  emptyDescription,
}: Props<T>) {
  return (
    <div
      style={{
        background: 'var(--surface-base)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-default)', background: 'var(--warm-gray-50)' }}>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  padding: '10px 16px',
                  textAlign: col.align ?? 'left',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600,
                  color: 'var(--text-tertiary)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  width: col.width,
                  whiteSpace: 'nowrap',
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                {columns.map((col) => (
                  <td key={col.key} style={{ padding: '12px 16px' }}>
                    <Skeleton height={16} width={col.width ? undefined : '80%'} />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: '48px 16px', textAlign: 'center' }}
              >
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontWeight: 500, fontSize: 'var(--text-sm)' }}>
                  {emptyMessage}
                </p>
                {emptyDescription && (
                  <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                    {emptyDescription}
                  </p>
                )}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr
                key={getKey(row, i)}
                style={{
                  borderBottom: i < rows.length - 1 ? '1px solid var(--border-subtle)' : undefined,
                  transition: 'background var(--transition-fast)',
                  cursor: onRowClick ? 'pointer' : undefined,
                }}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-hover)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      padding: '12px 16px',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-primary)',
                      textAlign: col.align ?? 'left',
                      verticalAlign: 'middle',
                    }}
                  >
                    {col.render(row, i)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
